import { useState, useCallback, useEffect } from "react";
import CalcalistPaywallCheckoutPlans, { SubscriptionPlan } from "./calcalistPaywallCheckoutPlans";
import CalcalistPaywallCheckoutPersonalDetails from "./calcalistPaywallCheckoutPersonalDetails";
import CalcalistPaywallCheckoutMultiStepsProgressbar from "./multiStepProgressbar/calcalistPaywallCheckoutMultiStepsProgressbar";
import CalcalistPaywallCheckoutPaymentDetails from "./calcalistPaywallCheckoutPaymentDetails";
import CalcalistPaywallCheckoutFinish from "./calcalistPaywallCheckoutPaymentFinish"
import { SettingLinks } from "../calcalistPaywallCheckout";
import { useRedirect } from "../../../../../../global/paywall/useRedirect";
import { AuthLogOutLocal, getPaywallSettingsFromLocalStorage, PAYWALL_LOCAL_STORAGE_KEY } from "../../../../../../global/paywall/authOperations";
import { getPaywallAccessData } from "../../../../../../global/paywall/paywallApiUrls";
import { CustomerAccessData } from "../../../../../../global/paywall/paywallInterfaces";
import * as  axios from 'axios';
import { hasProductClientId, productClientId } from "../../../../../../global/paywall/paywallUtils";
import { DetailsUpdateMode, DetailUpdateError, useDetailsUpdate } from "../../../../../../global/paywall/useDetailsUpdate";
import PaywallRevalidate from "../../../../paywallRevalidate/desktop/paywallRevalidate";


interface CalcalistPaywallCheckoutComponentaProps {
    settingLinks: SettingLinks;
}


enum CheckoutStep {
    SELECT_PLAN = "בחירת מסלול",
    PERSONAL_DETAILS = "פרטים אישיים",
    PAYMENT_DETAILS = "פרטי תשלום",
    FINISH = "סיום",
}


export const CalcalistPaywallCheckoutComponenta = (props: CalcalistPaywallCheckoutComponentaProps) => {

    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    const [selectedPlan, setPlan] = useState<SubscriptionPlan>(null);
    const [shouldRedirect, setShouldRedirect] = useState(false);
    const { settingLinks } = props;
    const [fallbackUrl, setFallbackUrl] = useState<string>(settingLinks.postPurchase.url);
    useRedirect({ fallbackUrl: fallbackUrl, enabled: shouldRedirect });
    const {
        customerDetails,
        setCustomerDetails,
        revalidationType,
        setRevalidationType,
        setAgreeToTermsOfUse,
        agreeToTermsOfUse,
        error,
        clearError,
        revalidateUser,
        revalidationStep,
        saveCustomerDetails,
        sendRevalidation,
        validateOtp
    } = useDetailsUpdate(DetailsUpdateMode.PURCHASE);

    const [hasCheckedAccess, setHasCheckedAccess] = useState(false);

    useEffect(() => {
        if (customerDetails.email && !hasCheckedAccess) {
            checkForAccess();
            setHasCheckedAccess(true);
        }
    }, [customerDetails, hasCheckedAccess]);

    useEffect(() => {
        if (error === DetailUpdateError.NOT_LOGGED_IN) {
            setFallbackUrl(settingLinks.authPage.url);
            AuthLogOutLocal();
            setShouldRedirect(true);
        }
    }, [error]);


    const checkForAccess = async () => {
        if (!customerDetails.email) {
            return;
        }
        try {
            const response = await axios.get<{ data: CustomerAccessData }>(getPaywallAccessData());
            const accessData = response && response.data && response.data.data as CustomerAccessData;
            const hasProducts = hasProductClientId(accessData.claims, productClientId);
            if (accessData && hasProducts) {
                setFallbackUrl(settingLinks.postPurchase.url);
                setShouldRedirect(true);
            }
        } catch (error) {
            console.error("Error fetching customer access data:", error);
        }
    }


    if (revalidateUser) {
        return <PaywallRevalidate
            userData={revalidateUser}
            step={revalidationStep}
            error={error}
            revalidationType={revalidationType}
            setRevalidationType={setRevalidationType}
            sendRevalidation={sendRevalidation}
            validateOtp={validateOtp}
        />;
    }


    const steps = Object.values(CheckoutStep);
    const nextStep = () => {
        setCurrentStepIndex(Math.min(currentStepIndex + 1, steps.length - 1));
    };

    const prevStep = () => {
        setCurrentStepIndex(Math.max(currentStepIndex - 1, 0));
    };

    const renderStepContent = () => {
        switch (steps[currentStepIndex]) {
            case CheckoutStep.SELECT_PLAN:
                return <CalcalistPaywallCheckoutPlans nextStep={nextStep} setPlan={setPlan} settingLinks={settingLinks} />;
            case CheckoutStep.PERSONAL_DETAILS:
                return <CalcalistPaywallCheckoutPersonalDetails
                    setCustomerDetails={setCustomerDetails}
                    customerDetails={customerDetails}
                    settingLinks={settingLinks}
                    setAgreeToTermsOfUse={setAgreeToTermsOfUse}
                    agreeToTermsOfUse={agreeToTermsOfUse}
                    error={error}
                    clearError={clearError}
                    saveCustomerDetails={saveCustomerDetails}
                    nextStep={nextStep}
                    prevStep={prevStep} />;
            case CheckoutStep.PAYMENT_DETAILS:
                return <CalcalistPaywallCheckoutPaymentDetails nextStep={nextStep} prevStep={prevStep} selectedPlan={selectedPlan} />;
            case CheckoutStep.FINISH:
                return <CalcalistPaywallCheckoutFinish setShouldRedirect={setShouldRedirect} nextStep={nextStep} />;
            default:
                return null;
        }
    };

    return (
        <div className="CalcalistPaywallCheckoutComponenta">
            <div className={`gradient-background ${Object.keys(CheckoutStep)[currentStepIndex] || ''}`}></div>
            <div className="calcalist-logo-container">
                <div className="calcalist-logo"></div>
                <div className="calcalist-digital-edition-text">המהדורה הדיגיטלית</div>
            </div>
            <CalcalistPaywallCheckoutMultiStepsProgressbar steps={steps} currentStepIndex={currentStepIndex} />
            {renderStepContent()}
        </div>
    )
}

CalcalistPaywallCheckoutComponenta.siteScriptName = "CalcalistPaywallCheckoutComponenta"
